// INVDESIGN1-O1 (docs/build-briefs/INVDESIGN1-ORDER-ordering-screens.md): the words and sums behind the four ordering screens,
// with no DOM, so each rule is tested on its own (test/invorder2-ordering-model.test.ts). Dates arrive as calendar days and are
// never parsed through a time zone; instants are shown in the location's own zone. Money is whole cents, rounded half up once
// per line, the way the server prices a line.

import type { OpenOrderDto, OrderingOrderLineDto } from "./api.ts";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

function parseDay(iso: string): { year: number; month: number; day: number; weekday: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (match === null) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  return { year, month, day, weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay() };
}

/** "2026-11-04" -> "Wed". */
export function dayName(iso: string | null): string {
  const parsed = iso === null ? null : parseDay(iso);
  return parsed === null ? "" : (DAYS[parsed.weekday] ?? "");
}

/** "2026-11-04" -> "Wed Nov 4". */
export function dayDate(iso: string | null): string {
  const parsed = iso === null ? null : parseDay(iso);
  return parsed === null ? "" : `${DAYS[parsed.weekday]} ${MONTHS[parsed.month - 1]} ${parsed.day}`;
}

/** "2026-11-04" -> "Nov 4". */
export function monthDay(iso: string | null): string {
  const parsed = iso === null ? null : parseDay(iso);
  return parsed === null ? "" : `${MONTHS[parsed.month - 1]} ${parsed.day}`;
}

/** Whole days from `fromIso` to `toIso` (both calendar days). */
export function daysBetween(fromIso: string, toIso: string): number | null {
  const from = parseDay(fromIso);
  const to = parseDay(toIso);
  if (from === null || to === null) return null;
  return Math.round(
    (Date.UTC(to.year, to.month - 1, to.day) - Date.UTC(from.year, from.month - 1, from.day)) / 86_400_000,
  );
}

/** "2:00 PM" -> "2 PM"; "2:30 PM" stays. */
export function shortTime(time: string): string {
  return time.replace(/:00 /, " ");
}

/** An instant as "Fri 4:12 PM" in the location's zone. Blank for null or an unreadable zone. */
export function sentAtText(instant: string | null, timeZone: string): string {
  if (instant === null) return "";
  const when = new Date(instant);
  if (Number.isNaN(when.getTime())) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
      hour: "numeric",
      minute: "2-digit",
    }).formatToParts(when);
    const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? "";
    return `${get("weekday")} ${get("hour")}:${get("minute")} ${get("dayPeriod")}`;
  } catch (error) {
    if (error instanceof RangeError) return "";
    throw error;
  }
}

/** One decimal for a count ("2.0", "1.4"); a par that is whole stays whole ("6"). */
export function countText(value: string | null, keepDecimal: boolean): string {
  if (value === null) return "";
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  return keepDecimal ? number.toFixed(1) : String(Math.round(number * 10) / 10);
}

/** "bottle" and 4 -> "bottles"; 1 -> "bottle". The unit is the vendor's, as placed. */
export function unitWord(orderUnit: string | null | undefined, quantity: number): string {
  const word = (orderUnit ?? "unit").trim().toLowerCase() || "unit";
  if (quantity === 1) return word;
  return /(s|x|z|ch|sh)$/.test(word) ? `${word}es` : `${word}s`;
}

/** The line's total in cents: quantity x price, rounded half up once. Null with no price. */
export function lineTotalCents(quantity: number, unitCostCents: number | null): number | null {
  if (unitCostCents === null || !Number.isFinite(quantity)) return null;
  return Math.round(quantity * unitCostCents);
}

export function sumCents(values: readonly (number | null)[]): number {
  return values.reduce<number>((sum, value) => sum + (value ?? 0), 0);
}

export interface StatusChip {
  text: string;
  tone: "neutral" | "info" | "success" | "warning" | "danger";
}

/** The status of an open order: Draft, not sent, or Sent with the time. */
export function orderStatusChip(
  order: Pick<OpenOrderDto, "status" | "sentAt">,
  timeZone: string,
): StatusChip {
  switch (order.status) {
    case "draft":
      return { text: "Draft, not sent", tone: "neutral" };
    case "placed": {
      const at = sentAtText(order.sentAt, timeZone);
      return { text: at === "" ? "Sent" : `Sent ${at}`, tone: "info" };
    }
    case "partially_received":
      return { text: "Part received", tone: "warning" };
    case "exception":
      return { text: "Needs review", tone: "danger" };
    case "received":
      return { text: "Received", tone: "success" };
    default:
      return { text: order.status, tone: "neutral" };
  }
}

export function orderTitle(order: { orderNumber: number | null }): string {
  return order.orderNumber === null ? "Order" : `Order ${order.orderNumber}`;
}

/** "2 kegs Birdsong sixth barrel, 1 case Miller Lite, 2 cases Yuengling" for up to three lines, else
 * "6 items: Svedka, Buffalo Trace, Woodford Reserve and 3 more". */
export function whatText(order: Pick<OpenOrderDto, "lines" | "lineCount">): string {
  const lines = order.lines;
  if (lines.length <= 3) {
    return lines
      .map((line) => {
        const quantity = Number(line.quantity);
        return `${line.quantity} ${unitWord(line.orderUnit, quantity)} ${line.productName}`;
      })
      .join(", ");
  }
  const names = lines.slice(0, 3).map((line) => line.productName);
  return `${lines.length} items: ${names.join(", ")} and ${lines.length - 3} more`;
}

/** The arrival cell of an open order: "Wed Nov 4, in 2 days" for a sent order, "Wed Nov 4 if sent by Tue 2 PM" for a draft. */
export function arrivalText(
  order: Pick<OpenOrderDto, "status" | "arrivalDate" | "orderByDate" | "orderByTime" | "orderByPast">,
  today: string,
): { lead: string; rest: string } {
  if (order.arrivalDate === null) return { lead: "", rest: "" };
  const date = dayDate(order.arrivalDate);
  if (order.status === "draft") {
    if (order.orderByPast || order.orderByDate === null || order.orderByTime === null) {
      return { lead: date, rest: " if sent today" };
    }
    const by = order.orderByDate === today ? "today" : dayName(order.orderByDate);
    return { lead: date, rest: ` if sent by ${by} ${shortTime(order.orderByTime)}` };
  }
  const days = daysBetween(today, order.arrivalDate);
  if (days === null) return { lead: date, rest: "" };
  if (days < 0) return { lead: date, rest: ", late" };
  if (days === 0) return { lead: date, rest: ", today" };
  return { lead: date, rest: `, in ${days} ${days === 1 ? "day" : "days"}` };
}

/** The header of a vendor card: "Order by Tue 2 PM, arrives Wed Nov 4". */
export function vendorScheduleText(
  schedule: { orderByDate: string; orderByTime: string; arrivalDate: string; past: boolean } | null,
  today: string,
): string {
  if (schedule === null) return "";
  if (schedule.past) return `Cutoff has passed, arrives ${dayDate(schedule.arrivalDate)}`;
  const by = schedule.orderByDate === today ? "today" : dayName(schedule.orderByDate);
  return `Order by ${by} ${shortTime(schedule.orderByTime)}, arrives ${dayDate(schedule.arrivalDate)}`;
}

/** The "On order" cell: "1 case, due Wed". Null when nothing is on order. */
export function onOrderText(
  onOrder: { quantity: string; orderUnit: string | null; arrivalDate: string | null } | null,
): string | null {
  if (onOrder === null) return null;
  const quantity = Number(onOrder.quantity);
  const base = `${onOrder.quantity} ${unitWord(onOrder.orderUnit, quantity)}`;
  return onOrder.arrivalDate === null ? base : `${base}, due ${dayName(onOrder.arrivalDate)}`;
}

// ---------------------------------------------------------------------
// Receiving (screen 10).
// ---------------------------------------------------------------------

export interface ReceiveLineState {
  line: OrderingOrderLineDto;
  /** Counted in the line's own unit. */
  received: number;
  /** A different product that arrived in place of this line's. */
  substitute: { liquorProductId: string; productName: string; quantity: number; unitCostCents: number } | null;
}

export function lineOrdered(state: ReceiveLineState): number {
  return Number(state.line.outstandingQuantity);
}

/** What the line has delivered this visit, in its unit: its own item plus any substitute. */
export function lineDelivered(state: ReceiveLineState): number {
  return state.received + (state.substitute?.quantity ?? 0);
}

export function lineShort(state: ReceiveLineState): number {
  return Math.max(lineOrdered(state) - lineDelivered(state), 0);
}

export interface ReceiveResult {
  text: string;
  tone: StatusChip["tone"];
}

/** The line's result chip: All here, "1 keg short", Not sent. */
export function receiveResult(state: ReceiveLineState): ReceiveResult {
  const ordered = lineOrdered(state);
  const delivered = lineDelivered(state);
  if (delivered <= 0) return { text: "Not sent", tone: "danger" };
  const short = ordered - delivered;
  if (short > 0) {
    const shortText = `${Number(short.toFixed(4))} ${unitWord(state.line.orderUnit, short)} short`;
    return { text: shortText, tone: "warning" };
  }
  if (short < 0) return { text: "Too many", tone: "danger" };
  return { text: "All here", tone: "success" };
}

export interface ReceiveSummary {
  orderedCents: number;
  /** Each line that arrived, in cents, in line order (the "$31.00 + $95.00 + $58.00" parts). */
  arrivedParts: number[];
  invoiceCents: number;
  /** "1 case Miller Lite, 1 keg Birdsong, 2 cases Miller High Life". */
  stockAdded: string;
  anyShort: boolean;
  /** Lines that are short after this delivery, for the "Is the rest still coming?" question. */
  shortLines: { productName: string; short: number; orderUnit: string | null }[];
}

export function summarizeReceiving(states: readonly ReceiveLineState[], orderedCents: number): ReceiveSummary {
  const parts: number[] = [];
  const added: string[] = [];
  const shortLines: ReceiveSummary["shortLines"] = [];
  for (const state of states) {
    const own = lineTotalCents(state.received, state.line.unitCostCents);
    if (state.received > 0) {
      parts.push(own ?? 0);
      added.push(
        `${Number(state.received.toFixed(4))} ${unitWord(state.line.orderUnit, state.received)} ${state.line.productName}`,
      );
    }
    if (state.substitute !== null && state.substitute.quantity > 0) {
      parts.push(lineTotalCents(state.substitute.quantity, state.substitute.unitCostCents) ?? 0);
      added.push(
        `${Number(state.substitute.quantity.toFixed(4))} ${unitWord(state.line.orderUnit, state.substitute.quantity)} ${state.substitute.productName}`,
      );
    }
    const short = lineShort(state);
    if (short > 0) {
      shortLines.push({ productName: state.line.productName, short, orderUnit: state.line.orderUnit });
    }
  }
  return {
    orderedCents,
    arrivedParts: parts,
    invoiceCents: sumCents(parts),
    stockAdded: added.join(", "),
    anyShort: shortLines.length > 0,
    shortLines,
  };
}

/** "Birdsong, 1 keg short" or "Birdsong 1 keg, Yuengling 2 cases short" for the rest-is-coming question. */
export function shortHeadline(shortLines: ReceiveSummary["shortLines"]): string {
  const part = (line: ReceiveSummary["shortLines"][number]): string =>
    `${Number(line.short.toFixed(4))} ${unitWord(line.orderUnit, line.short)}`;
  if (shortLines.length === 1) {
    const only = shortLines[0] as ReceiveSummary["shortLines"][number];
    return `${only.productName}, ${part(only)} short`;
  }
  return `${shortLines.map((line) => `${line.productName} ${part(line)}`).join(", ")} short`;
}

/** The sentence under the invoice total: what stock was added and how the order will read. */
export function receivingOutcomeText(
  summary: ReceiveSummary,
  decision: "later" | "close" | null,
  formatMoney: (cents: number) => string,
): string {
  const added = summary.stockAdded === "" ? "Nothing added yet." : `Stock added: ${summary.stockAdded}.`;
  if (!summary.anyShort) return `${added} Order will read Received, ${formatMoney(summary.invoiceCents)}.`;
  if (decision === "close") {
    return `${added} Order will read Received, short, ${formatMoney(summary.invoiceCents)}.`;
  }
  if (decision === "later") {
    return `${added} Order stays open for what is still coming.`;
  }
  return `${added} Say whether the rest is coming.`;
}

/** The whole numbers a stepper walks through. */
export function stepQuantity(current: number, delta: 1 | -1, min: number): number {
  return Math.max(min, Math.round(current) + delta);
}
